import * as React from "react";
import {Label, Tooltip} from "@vidazoo/ui-kit";
import clsx from "clsx";
import theme from "./theme.module.scss";
import {IReportingPreparedField} from "../../../common/interfaces/IReportingPreparedResults";
import {extractReportingFieldValue} from "../../../common/utils/extractReportingFieldValue";
import {ReportFieldValueType} from "../../../common/enums/ReportFieldValueType";
import {baselineDelta, formatBaselinePct} from "../../../common/utils/baselineDelta";

const ARROWS = {up: "▲", down: "▼", same: "="};

export interface IBaselineDeltaBadgeProps {
    field: IReportingPreparedField;
    baselineField?: IReportingPreparedField;
}

/** Neutral up/down and percent change against the baseline row; it does not judge good or bad. */
export default React.memo(function BaselineDeltaBadge({field, baselineField}: IBaselineDeltaBadgeProps): React.JSX.Element {
    const delta = baselineField ? baselineDelta(field.value, baselineField.value) : null;
    if (!delta) return null;

    const label = delta.direction === "same" ? "0%" : formatBaselinePct(delta.pct);
    const display = (item: IReportingPreparedField) =>
        extractReportingFieldValue(item, "displayValue", ReportFieldValueType.ORIGINAL);

    const tooltip = (
        <div className={theme.popover}>
            <div className={theme.grid}>
                <div className={theme.item}>
                    <Label weight="bold" className={theme.title} size={10} uppercase>This row</Label>
                    <div className={theme.value}>{display(field)}</div>
                </div>
                <div className={theme.item}>
                    <Label weight="bold" className={theme.title} size={10} uppercase>Baseline</Label>
                    <div className={theme.value}>{display(baselineField)}</div>
                </div>
            </div>
        </div>
    );

    return (
        <Tooltip title={tooltip}>
            <span className={clsx(theme.baselineDelta, theme[delta.direction])} data-baseline-delta={delta.direction}
                  aria-label={`${delta.direction === "same" ? "No change" : delta.direction === "up" ? "Up" : "Down"} ${label} from baseline`}>
                <span className={theme.arrow} aria-hidden="true">{ARROWS[delta.direction]}</span>{label}
            </span>
        </Tooltip>
    );
});
